import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DatePicker, Input, Space } from 'antd';
import dayjs from 'dayjs';

import { CALENDAR, CHEVRON_LEFT, CHEVRON_RIGHT, CLOCK } from '../icons';
import useDateFormat from '../../hooks/useDateFormat';
import useRelativeTime from '../../hooks/useRelativeTime';
import { MINUTE_STEPS as MINUTE_STEP_VALUES } from '../../utils/dateBounds';
import { isWeekdayAllowed, normalizeWeekdays } from '../../utils/dateOptions';
import { parseDateInput } from '../../utils/dateParse';
import { stepDateTimeValue, stepDateValue } from '../../utils/elements';
import {
	formatDateByPrecision,
	formatDisplayDateTime,
	formatDisplayTime,
	getTimeFormat,
	parseStoredDate,
	parseStoredTime,
	toStoredDate,
	toStoredDateTime,
	toStoredTime,
} from '../../utils/dateDisplay';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import useWindowClass from '../../hooks/useWindowClass';

/**
 * The platform's date, date-time and time field. `InputDate` and `InputTime`
 * are thin wrappers over it, so every call site keeps its props.
 *
 * KEYBOARD FIRST, CALENDAR SECOND. The primary control is a plain text box.
 * The calendar was never the problem — entry was: the old field made you
 * click through a popup for "tomorrow", and on a touch screen it was
 * `inputReadOnly`, so a phone could not type a date at all. Here you type,
 * the passive suffix confirms what was understood ("→ Mon, Sep 14") before
 * anything is committed, and Enter or blur commits it. Text that is not a
 * date NEVER clears the field: the previous value stands, the box turns
 * `status="error"` and keeps the text so it can be fixed.
 *
 * The calendar is one suffix button away (and on ⌥↓). antd's own picker
 * renders it — nothing about the calendar is hand rolled — from a clipped,
 * click-proof anchor under the field, so the popup lands where it always did
 * while the picker's own input stays out of the way.
 *
 * PHONES AND TOUCH get the browser's native `<input type=date|datetime-local
 * |time>` instead, because the OS wheel is the best mobile date UX there is
 * and it needs no typing at all; "Type it…" swaps the grammar back in for
 * anyone who wants it.
 *
 * STORED SHAPES (utils/dateDisplay): `date` commits 'YYYY-MM-DD' — the day
 * that was picked, not a timestamp whose UTC day the API would slice a day
 * early for everyone east of UTC — `time` commits 'HH:mm', and `date-time`
 * commits a full ISO instant. Legacy timestamps are read wherever a value
 * comes in.
 *
 * AFFIXES follow the platform rule: action buttons (the step chevrons) sit
 * rightmost in a `Space.Compact`, passive text (the parse confirmation, the
 * relative time) sits INSIDE the input, and the typed value has priority —
 * the suffix is what shrinks (see css/components/inputs.css).
 */

const MODES = { DATE: 'date', DATE_TIME: 'date-time', TIME: 'time' };

// One example of the grammar this field takes, per shape it can wear.
const PLACEHOLDERS = {
	[ MODES.TIME ]: 'e.g. 3pm',
	[ MODES.DATE ]: 'e.g. tomorrow, +7d, 9/14',
	[ MODES.DATE_TIME ]: 'e.g. tomorrow, +7d, 9/14',
	month: 'e.g. sep 2026, +3mo',
	year: 'e.g. 2026, +1y',
};

/**
 * A fingertip is the primary pointer. NOT the old picker's
 * `document.createEvent('TouchEvent')` probe, which succeeds in any browser
 * that merely KNOWS about touch events (desktop Chrome included) — that is
 * how the old field ended up read-only for people with a mouse. `pointer:
 * coarse` asks the question that matters, and a touch laptop driven by its
 * trackpad correctly answers no.
 */
const isCoarsePointer = () => {
	try {
		return !! ( window.matchMedia && window.matchMedia( '(pointer: coarse)' ).matches );
	}
	catch ( e ) {
		return false;
	}
};

/**
 * The stored value as the words the field shows when nothing is being typed.
 * `precision` ('day' | 'month' | 'year') is a `date` element's reading grain:
 * the value under it is still a whole day, only the reading is coarse.
 */
export function formatStoredValue ( mode, value, precision = 'day' ) {
	if ( value === null || value === undefined || value === '' ) return '';
	if ( mode === MODES.TIME ) return formatDisplayTime( value );
	if ( mode === MODES.DATE_TIME ) return formatDisplayDateTime( value );
	return formatDateByPrecision( value, precision );
}

/** The first day of the span a precision names — what a coarse field stores. */
function snapToPrecision ( mode, moment, precision ) {
	if ( ! moment || mode !== MODES.DATE ) return moment;
	if ( precision === 'month' ) return moment.startOf( 'month' );
	if ( precision === 'year' ) return moment.startOf( 'year' );
	return moment;
}

/** The stored value as the dayjs the antd picker takes. */
function pickerMoment ( mode, value ) {
	if ( value === null || value === undefined || value === '' ) return null;
	if ( mode === MODES.TIME ) return parseStoredTime( value );
	if ( mode === MODES.DATE ) return parseStoredDate( value );
	const parsed = dayjs( value );
	return parsed.isValid() ? parsed : null;
}

/** A dayjs as the shape this mode stores. */
function storedFromMoment ( mode, moment ) {
	if ( ! moment ) return null;
	if ( mode === MODES.TIME ) return toStoredTime( moment );
	if ( mode === MODES.DATE ) return toStoredDate( moment );
	return toStoredDateTime( moment );
}

/** The stored value as the browser's native input spelling, and back. */
function nativeFromStored ( mode, value, precision = 'day' ) {
	const moment = pickerMoment( mode, value );
	if ( ! moment ) return '';
	if ( mode === MODES.TIME ) return moment.format( 'HH:mm' );
	// `<input type="month">` speaks 'YYYY-MM' — the OS wheel a month-precision
	// field wants on a phone. (A YEAR has no native input at all, which is why
	// the typed control stands in for it below.)
	if ( mode === MODES.DATE ) return moment.format( precision === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD' );
	return moment.format( 'YYYY-MM-DDTHH:mm' );
}

function storedFromNative ( mode, text, precision = 'day' ) {
	if ( ! text ) return null;
	// A native month input hands back 'YYYY-MM'; the stored value is always a
	// whole day, so it is the first of that month.
	if ( mode === MODES.DATE && precision === 'month' ) return `${ text }-01`;
	// A native date and time input already speak the stored spelling.
	if ( mode === MODES.DATE || mode === MODES.TIME ) return text;
	const parsed = dayjs( text );
	return parsed.isValid() ? toStoredDateTime( parsed ) : null;
}

/** 0..n-1, for antd's disabled-hours / disabled-minutes lists. */
const countTo = n => Array.from( { length: n }, ( _, i ) => i );

export default function DateField ( {
	mode = MODES.DATE,
	elementName,
	elementLabel = '',
	value,
	onChange,
	disabled = false,
	required = false,
	incrementButtons = false,
	showRelativeTime = false,
	// The element's resolved Earliest / Latest (utils/dateBounds
	// resolveBounds): `{ moment, inclusive, label }` or null. They grey the
	// calendar and the clock here; the MESSAGE for a value that misses them is
	// the form's (utils/elementValidation), so one sentence says it once.
	min = null,
	max = null,
	// 1 | 5 | 15 | 30 — the clock's minute column and what the step buttons
	// move by, so a field that only takes quarter hours cannot be stepped off
	// one.
	minuteStep = undefined,
	// The element's `weekdays` set (utils/dateOptions), 0 = Sunday … 6: the
	// calendar greys every other day and the native input cannot reach one
	// (the MESSAGE is the form's, as with the bounds).
	weekdays = null,
	// 'day' | 'month' | 'year' — a `date` element's reading grain. The
	// calendar opens in that mode, the grammar takes "sep 2026" / "2026", and
	// the COMMITTED value is still a whole day.
	precision = 'day',
	status = undefined,
	wrapper: WrapperComponent = null,
	wrapperProps = {},
} ) {

	useDateFormat(); // display, parsing and the popup's clock all follow the preference

	const grain = mode === MODES.DATE ? precision : 'day';
	const coarse = grain === 'month' || grain === 'year';
	const allowedDays = mode === MODES.DATE ? normalizeWeekdays( weekdays ) : null;

	const { compact: phone } = useWindowClass();
	// A YEAR has no native input, so a coarse-year field keeps the typed
	// control even on a phone: typing "2026" is four keystrokes, and a date
	// wheel spun to a day nobody will read is worse than none.
	const prefersNative = ( phone || isCoarsePointer() ) && grain !== 'year';

	// `null` means "show the committed value"; a string is what is being typed.
	const [ draft, setDraft ] = useState( null );
	const [ invalid, setInvalid ] = useState( false );
	const [ open, setOpen ] = useState( false );
	const [ typedOnTouch, setTypedOnTouch ] = useState( false );
	const pickerRef = useRef( null );

	// Anything that changes the value from outside (a step button, the
	// calendar, the form resetting) ends the edit in progress.
	useEffect( () => {
		setDraft( null );
		setInvalid( false );
	}, [ value ] );

	const display = formatStoredValue( mode, value, grain );
	const text = draft === null ? display : draft;

	const commitStored = useCallback( next => {
		setDraft( null );
		setInvalid( false );
		onChange( elementName, next ?? null );
	}, [ elementName, onChange ] );

	const commitMoment = useCallback( moment => {
		commitStored( storedFromMoment( mode, snapToPrecision( mode, moment, grain ) ) );
	}, [ commitStored, mode, grain ] );

	const commitText = useCallback( raw => {
		const trimmed = String( raw ?? '' ).trim();
		if ( ! trimmed ) {
			commitStored( null );
			return;
		}
		// Blurring out of an untouched field is not an edit.
		if ( trimmed === display.trim() ) {
			setDraft( null );
			setInvalid( false );
			return;
		}
		const parsed = parseDateInput( trimmed, { mode, precision: grain } );
		if ( ! parsed ) {
			// Never silently clear: keep the value AND the text.
			setInvalid( true );
			return;
		}
		commitStored( parsed.value );
	}, [ commitStored, display, mode, grain ] );

	// What the field understood, shown while it is being typed. Parsing the
	// text the field itself printed would only echo it back, so the
	// confirmation waits until the text differs from the committed value.
	const confirmation = useMemo( () => {
		if ( draft === null ) return '';
		const trimmed = draft.trim();
		if ( ! trimmed || trimmed === display.trim() ) return '';
		const parsed = parseDateInput( trimmed, { mode, precision: grain } );
		return parsed ? '→ ' + parsed.confirm : '';
	}, [ draft, display, mode, grain ] );

	const relativeText = useRelativeTime( value, {
		enabled: showRelativeTime && mode !== MODES.TIME,
		compact: phone,
		// A plain date reads in days ("today", "3 days ago"); a date-time as
		// an instant ("4 minutes ago").
		dataType: mode === MODES.DATE ? 'date' : 'date-time',
	} );

	// One passive slot, two tenants: the confirmation wins while something is
	// being typed, the relative time holds it the rest of the time.
	const suffixText = confirmation || relativeText;

	const openPicker = useCallback( () => {
		if ( disabled ) return;
		setOpen( true );
		// antd focuses its own input on open; doing it here as well keeps the
		// calendar keyboard-navigable when the popup was opened from ⌥↓.
		window.setTimeout( () => {
			try { pickerRef.current?.focus(); } catch ( e ) { /* the picker may have closed */ }
		}, 0 );
	}, [ disabled ] );

	const handleKeyDown = event => {
		if ( event.altKey && event.key === 'ArrowDown' ) {
			event.preventDefault();
			openPicker();
			return;
		}
		if ( event.key === 'Enter' ) {
			event.preventDefault();
			commitText( text );
		}
	};

	const handleInputChange = event => {
		const next = event.target.value;
		setInvalid( false );
		// The clear button (and emptying the box by hand) commits null right
		// away: an empty field should never look full the moment it blurs.
		if ( ! next ) {
			commitStored( null );
			return;
		}
		setDraft( next );
	};

	// ---- step buttons (the `incrementButtons` element option) ----

	const stepFrom = useCallback( ( amount, unit ) => {
		const base = pickerMoment( mode, value ) || dayjs();
		commitMoment( base.add( amount, unit ) );
	}, [ commitMoment, mode, value ] );

	// A coarse field steps by its OWN grain: a month-precision date stepped by
	// a day would land inside the month it already shows and look broken.
	const handleDayStep = direction => {
		if ( ! coarse ) {
			commitStored( stepDateValue( value, direction ) );
			return;
		}
		const base = pickerMoment( mode, value );
		commitMoment( base ? base.add( direction, grain ) : dayjs() );
	};

	const handleMinuteStep = ( direction, minutes ) =>
		commitStored( toStoredDateTime( stepDateTimeValue( value, direction, minutes ) ) );

	// The minutes the steppers move by. The smallest is the element's own
	// minute step — a field that takes quarter hours must not be stepped onto
	// 3:07 — and the rest are the multiples of it that used to be hard coded.
	const step = MINUTE_STEP_VALUES.includes( minuteStep ) ? minuteStep : 1;
	const stepMinutes = [ ...new Set( [ step, 15, 60 ].filter( m => m >= step && m % step === 0 ) ) ].sort( ( a, b ) => a - b );

	const stepButtons = mode === MODES.DATE_TIME ? [
		...[ ...stepMinutes ].reverse().map( m => (
			{ label: `-${ m }`, ariaLabel: `${ elementLabel }: -${ m } minute${ m === 1 ? '' : 's' }`, onClick: () => handleMinuteStep( -1, m ) }
		) ),
		{ label: 'Now', ariaLabel: `${ elementLabel }: set to now`, onClick: () => commitMoment( dayjs() ) },
		...stepMinutes.map( m => (
			{ label: `+${ m }`, ariaLabel: `${ elementLabel }: +${ m } minute${ m === 1 ? '' : 's' }`, onClick: () => handleMinuteStep( 1, m ) }
		) ),
	] : [
		{ icon: <CHEVRON_LEFT />, ariaLabel: `${ elementLabel }: previous ${ grain }`, onClick: () => handleDayStep( -1 ) },
		{ icon: <CHEVRON_RIGHT />, ariaLabel: `${ elementLabel }: next ${ grain }`, onClick: () => handleDayStep( 1 ) },
	];

	const showSteps = incrementButtons && mode !== MODES.TIME;

	// ---- the calendar / clock popup ----

	const use12Hours = getTimeFormat() === '12h';
	const clockFormat = use12Hours ? 'h:mm a' : 'HH:mm';
	const showTimeObj = { format: clockFormat, use12Hours, minuteStep: step };

	// ---- the bounds, on the calendar and the clock ----

	/**
	 * A DAY is greyed only when no value inside it could be accepted: for a
	 * `date` that is the day itself, for a `date-time` the whole day, because
	 * the clock below still narrows the hours on the edge days.
	 */
	const disabledDate = ( min || max || allowedDays ) ? current => {
		if ( ! current ) return false;
		// The WEEKDAY rule greys days, so it only speaks where days are what
		// the calendar is showing (a month or year picker has none, and a
		// date & time has no weekday option at all).
		if ( allowedDays && ! coarse && ! isWeekdayAllowed( current, allowedDays ) ) return true;
		if ( mode === MODES.DATE ) {
			if ( min && ( min.inclusive ? current.isBefore( min.moment, 'day' ) : ! current.isAfter( min.moment, 'day' ) ) ) return true;
			if ( max && ( max.inclusive ? current.isAfter( max.moment, 'day' ) : ! current.isBefore( max.moment, 'day' ) ) ) return true;
			return false;
		}
		if ( min && current.endOf( 'day' ).isBefore( min.moment ) ) return true;
		if ( max && current.startOf( 'day' ).isAfter( max.moment ) ) return true;
		return false;
	} : undefined;

	const disabledTime = ( ( min || max ) && mode !== MODES.DATE ) ? current => {
		const reference = current && current.isValid && current.isValid() ? current : dayjs();
		// A bound on another day does not narrow this day's clock — the
		// calendar has already said whether the day itself is allowed.
		const lower = min && ( mode === MODES.TIME || reference.isSame( min.moment, 'day' ) ) ? min.moment : null;
		const upper = max && ( mode === MODES.TIME || reference.isSame( max.moment, 'day' ) ) ? max.moment : null;
		if ( ! lower && ! upper ) return {};
		return {
			disabledHours: () => countTo( 24 ).filter( hour => (
				( lower && hour < lower.hour() ) || ( upper && hour > upper.hour() )
			) ),
			disabledMinutes: hour => countTo( 60 ).filter( minute => (
				( lower && hour === lower.hour() && minute < lower.minute() )
				|| ( upper && hour === upper.hour() && minute > upper.minute() )
			) ),
		};
	} : undefined;

	// The native input's own limits. A strict bound is nudged one unit in, so
	// the browser refuses exactly what the validator would.
	const nativeLimit = ( bound, direction ) => {
		if ( ! bound ) return undefined;
		const moment = bound.inclusive
			? bound.moment
			: bound.moment.add( direction, mode === MODES.DATE ? 'day' : 'minute' );
		if ( mode === MODES.TIME ) return moment.format( 'HH:mm' );
		if ( mode === MODES.DATE ) return moment.format( grain === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD' );
		return moment.format( 'YYYY-MM-DDTHH:mm' );
	};

	const coarseFooter = [
		{ label: `-1 ${ grain }`, onClick: () => stepFrom( -1, grain ) },
		{ label: grain === 'month' ? 'This month' : 'This year', onClick: () => commitMoment( dayjs() ), primary: true },
		{ label: `+1 ${ grain }`, onClick: () => stepFrom( 1, grain ) },
	];

	const footerButtons = () => {
		const buttons = mode === MODES.DATE ? ( coarse ? coarseFooter : [
			{ label: '-1 day', onClick: () => stepFrom( -1, 'day' ) },
			{ label: 'Today', onClick: () => commitMoment( dayjs() ), primary: true },
			{ label: '+1 day', onClick: () => stepFrom( 1, 'day' ) },
		] ) : [
			{ label: '-1 hour', onClick: () => stepFrom( -1, 'hour' ) },
			{ label: '-5 min', onClick: () => stepFrom( -5, 'minute' ) },
			{ label: 'Now', onClick: () => commitMoment( dayjs() ), primary: true },
			{ label: '+5 min', onClick: () => stepFrom( 5, 'minute' ) },
			{ label: '+1 hour', onClick: () => stepFrom( 1, 'hour' ) },
		];
		return (
			<div className='d-flex flex-wrap'>
				{buttons.map( btn => (
					<Button
						key={btn.label}
						className='p-1 m-1'
						type={btn.primary ? 'default' : 'text'}
						onClick={btn.onClick}
					>
						{btn.label}
					</Button>
				) )}
			</div>
		);
	};

	const commonPickerProps = {
		ref: pickerRef,
		disabledDate,
		disabledTime,
		open,
		onOpenChange: next => setOpen( next ),
		onChange: next => {
			commitMoment( next && dayjs( next ).isValid() ? dayjs( next ) : null );
			setOpen( false );
		},
		value: pickerMoment( mode, value ),
		disabled,
		allowClear: false,
		needConfirm: false,
		showNow: false,
		renderExtraFooter: footerButtons,
		// Out of the tab order but still focusable, so ⌥↓ can hand the
		// calendar the keyboard without Tab ever landing on a clipped input.
		tabIndex: -1,
		// A format FUNCTION prints nothing into the picker's own input (and
		// makes antd treat it as read-only, which it is). The clipped input is
		// invisible, but it is still an input: leaving the value in it would
		// put a second copy of every date on the page for anything that reads
		// the DOM — a by-value query, a screen reader, an autofill guess.
		format: () => '',
	};

	// A clock is `DatePicker picker='time'` — the component `TimePicker` is —
	// written out so the columns come from `showTime` while the blanked
	// `format` above still keeps the clipped input empty.
	const hiddenPicker = (
		<span className='date-field__picker-anchor'>
			<DatePicker
				{...commonPickerProps}
				// A coarse `date` opens antd's own month or year panel — the
				// calendar for the grain that is being asked for, not a grid
				// of days one of which will silently stand for the month.
				picker={mode === MODES.TIME ? 'time' : ( coarse ? grain : 'date' )}
				showTime={mode === MODES.DATE ? false : showTimeObj}
			/>
		</span>
	);

	// ---- the controls ----

	const PickerIcon = mode === MODES.TIME ? CLOCK : CALENDAR;

	// The Wrapper's own <label for=…> names the control; naming it twice would
	// give every date field two accessible labels.
	const ariaLabel = WrapperComponent ? undefined : ( elementLabel || undefined );
	const pickerButtonLabel = `${ elementLabel }${ elementLabel ? ': ' : '' }${ mode === MODES.TIME ? 'open clock' : 'open calendar' }`;

	const typedControl = (
		<span className={'date-field date-field--' + mode + ( showSteps ? ' input-affix-group__input' : '' )}>
			<Input
				{...NO_TEXT_ASSIST}
				id={elementName}
				name={elementName}
				size='large'
				className='date-field__input'
				value={text}
				status={( invalid || status === 'error' ) ? 'error' : undefined}
				disabled={disabled}
				required={required}
				allowClear
				autoComplete='off'
				aria-label={ariaLabel}
				placeholder={PLACEHOLDERS[ coarse ? grain : mode ]}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onBlur={() => commitText( text )}
				suffix={<>
					{suffixText ? <span className='input-relative-suffix'>{suffixText}</span> : null}
					<Button
						type='text'
						size='small'
						className='date-field__picker-btn'
						icon={<PickerIcon />}
						disabled={disabled}
						// `title`, not `aria-label`: it is the button's accessible
						// name either way, and a label that repeats the field's
						// would make every by-label query on a form ambiguous.
						title={pickerButtonLabel}
						onMouseDown={event => event.preventDefault()}
						onClick={openPicker}
					/>
				</>}
			/>
			{hiddenPicker}
		</span>
	);

	const nativeControl = (
		<span className={'date-field date-field--native date-field--' + mode + ( showSteps ? ' input-affix-group__input' : '' ) + ( status === 'error' ? ' date-field--error' : '' )}>
			<input
				id={elementName}
				name={elementName}
				type={mode === MODES.DATE_TIME ? 'datetime-local' : ( grain === 'month' ? 'month' : mode )}
				className='date-field__native'
				value={nativeFromStored( mode, value, grain )}
				disabled={disabled}
				required={required}
				aria-label={ariaLabel}
				min={nativeLimit( min, 1 )}
				max={nativeLimit( max, -1 )}
				step={mode === MODES.DATE ? undefined : step * 60}
				onChange={event => commitStored( storedFromNative( mode, event.target.value, grain ) )}
			/>
			{suffixText ? <span className='input-relative-suffix'>{suffixText}</span> : null}
		</span>
	);

	const native = prefersNative && ! typedOnTouch;
	const control = native ? nativeControl : typedControl;

	const body = showSteps ? (
		<Space.Compact className='input-affix-group input-affix-group--stack' block>
			{control}
			{stepButtons.map( step => (
				<Button
					key={step.ariaLabel}
					size='large'
					className='input-affix-group__step-btn'
					icon={step.icon}
					disabled={disabled}
					onClick={step.onClick}
					aria-label={step.ariaLabel}
				>
					{step.label}
				</Button>
			) )}
		</Space.Compact>
	) : control;

	const inner = <>
		{body}
		{prefersNative ? (
			<button
				type='button'
				className='date-field__mode-toggle'
				disabled={disabled}
				onClick={() => setTypedOnTouch( prev => ! prev )}
			>
				{native ? 'Type it…' : 'Use the picker'}
			</button>
		) : null}
	</>;

	return WrapperComponent ? <WrapperComponent {...wrapperProps}>{inner}</WrapperComponent> : inner;

}
